import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import SettingsService from '@/services/2.6/SettingsService';
import WebEnvConfigService from '@/services/TheWebEnvConfigService';
import IUserSettings, { IProductGuideSteps, ISourceColumnOrder } from '@/models/IUserSettings';
import { AxiosResponse } from 'axios';

export const UserSettingsMutations = {
    Update: 'mutateUpdate',
    ExportAllComps: 'mutateExportAllComps',
    SuggestDataPreferenceUpdate: 'mutateSuggestDataPreferenceUpdate',
    PreferredCasing: 'mutatePreferredCasing',
    DisplayDataCascadeColumn: 'mutateDisplayDataCascadeColumn',
    DataSourceColumnOrder: 'mutateDataSourceColumnOrder',
    DataSourceOrderInitialized: 'mutateDataSourceOrderInitialized',
    SubjectCascadeDisabled: 'mutateSubjectCascadeDisabled',
    UserTutorialCompleted: 'mutateUserTutorialCompleted',
    ProductGuideStep: 'mutateProductGuideSteps',
    DisplayProductGuide: 'mutateDisplayProductGuide',
    ShareMySettings: 'mutateShareMySettings'
};

@Module({namespaced: true, name: 'userSettings'})
export default class UserSettingsStoreModule extends VuexModule/*<IUserSettings>*/ {
    private settings?: IUserSettings;

    get UserSettings(): IUserSettings | null {
        if (this.settings) {
            return this.settings;
        }
        return null;
    }
    get DataSourceColumnOrder(): ISourceColumnOrder[] {
        if (!this.settings?.dataSourceColumnOrder) return [];
        return this.settings.dataSourceColumnOrder.sort((a: any, b: any) => (a.order > b.order ? 1 : -1));
    }
    get DataSourceOrderInitialized(): boolean {
        return !!this.settings?.dataSourceOrderInitialized;
    }
    get DisplayDataCascadeColumn(): boolean {
        return !!this.settings?.displayDataCascadeColumn;
    }
    get DisplayProductGuide(): boolean {
        return !!this.settings?.displayProductGuide;
    }
    get ExportAllComps(): boolean {
        return !!this.settings?.exportAllComps;
    }
    get PreferredCasing(): string {
        return this.settings?.preferredCasing ?? 'Default';
    }
    get ProductGuideSteps(): IProductGuideSteps[] {
        return this.settings?.productGuideSteps ?? [];
    }
    get SubjectCascadeDisabled(): boolean {
        return !!this.settings?.subjectCascadeDisabled;
    }
    get SuggestDataPreferenceUpdate(): boolean {
        return !!this.settings?.suggestDataPreferenceUpdate;
    }
    get SuggestFormattingRule(): boolean {
        return !!this.settings?.suggestFormattingRule;
    }
    get UserTutorialCompleted(): boolean {
        return !!this.settings?.userTutorialCompleted;
    }
    get ShareMySettings(): boolean {
        return !!this.settings?.shareMySettings;
    }
    get SharedSettings(): any {
        return this.settings?.sharedSettings ?? {};
    }

    @Action
    async FetchSettings() {
        const webEnvConfig = await WebEnvConfigService.getInstance().getConfigByHost();
        const res = await new SettingsService(webEnvConfig).getUserSettings();

        this.context.commit(UserSettingsMutations.Update, res);
    }

    @Action
    async setExportAllComps(payload: { ExportAllComps: boolean }) {
        const res = await storeUserSettings(payload);

        if (res.status === 200) {
            this.context.commit(UserSettingsMutations.ExportAllComps, payload.ExportAllComps);
        } else {
            console.log('UserSettingsActions.ExportAllComps bad response');
        }
    }

    @Action
    async setSuggestDataPreferenceUpdate(payload: { SuggestDataPreferenceUpdate: boolean }) {
        const res = await storeUserSettings(payload);

        if (res.status === 200) {
            this.context.commit(UserSettingsMutations.SuggestDataPreferenceUpdate, payload.SuggestDataPreferenceUpdate);
        } else {
            console.log('UserSettingsActions.SuggestDataPreferenceUpdate bad response');
        }
    }

    @Action
    async setPreferredCasing(payload: { PreferredCasing: string }) {
        const res = await storeUserSettings(payload);

        if (res.status === 200) {
            this.context.commit(UserSettingsMutations.PreferredCasing, payload.PreferredCasing);
        } else {
            console.log('UserSettingsActions.PreferredCasing bad response');
        }
    }

    @Action
    async setDataSourceColumnOrder(payload: { DataSourceColumnOrder: ISourceColumnOrder[] }) {
        const res = await storeUserSettings(payload);

        if (res.status === 200) {
            this.context.commit(UserSettingsMutations.DataSourceColumnOrder, payload.DataSourceColumnOrder);
        } else {
            console.log('UserSettingsActions.DataSourceColumnOrder bad response');
        }
    }

    @Action
    async setDisplayDataCascadeColumn(payload: { DisplayDataCascadeColumn: boolean }) {
        const res = await storeUserSettings(payload);

        if (res.status === 200) {
            this.context.commit(UserSettingsMutations.DisplayDataCascadeColumn, payload.DisplayDataCascadeColumn);
        } else {
            console.log('UserSettingsActions.DisplayDataCascadeColumn bad response');
        }
    }

    @Action
    async setDataSourceOrderInitialized(payload: { dataSourceOrderInitialized: boolean }) {
        const res = await storeUserSettings(payload);

        if (res.status === 200) {
            this.context.commit(UserSettingsMutations.DataSourceOrderInitialized, payload.dataSourceOrderInitialized);
        } else {
            console.log('UserSettingsActions.DataSourceOrderInitialized bad response');
        }
    }

    @Action
    async setSubjectCascadeDisabled(payload: { SubjectCascadeDisabled: boolean }) {
        const res = await storeUserSettings(payload);

        if (res.status === 200) {
            this.context.commit(UserSettingsMutations.SubjectCascadeDisabled, payload.SubjectCascadeDisabled);
        } else {
            console.log('UserSettingsActions.SubjectCascadeDisabled bad response');
        }
    }

    @Action
    async setUserTutorialCompleted(payload: { userTutorialCompleted: boolean }) {
        const res = await storeUserSettings(payload);

        if (res.status === 200) {
            this.context.commit(UserSettingsMutations.UserTutorialCompleted, payload.userTutorialCompleted);
        } else {
            console.log('UserSettingsActions.UserTutorialCompleted bad response');
        }
    }

    @Action
    async setProductGuideSteps(payload: { ProductGuideSteps: IProductGuideSteps[] }) {
        const res = await storeUserSettings(payload);

        if (res.status !== 200) {
            console.log('UserSettingsActions.ProductGuideSteps bad response');
        }
    }

    @Action
    async ProductGuideStepUpdate(id: number) {
        this.context.commit(UserSettingsMutations.ProductGuideStep, id);
    }

    @Action
    async setDisplayProductGuide(payload: { DisplayProductGuide: boolean }) {
        const res = await storeUserSettings(payload);

        if (res.status === 200) {
            this.context.commit(UserSettingsMutations.DisplayProductGuide, payload.DisplayProductGuide);
        } else {
            console.log('UserSettingsActions.DisplayProductGuide bad response');
        }
    }

    @Action
    async setShareMySettings(payload: { ShareMySettings: boolean }) {
        const webEnvConfig = await WebEnvConfigService.getInstance().getConfigByHost();
        const res = await new SettingsService(webEnvConfig).saveUserSettings(payload);

        if (res.status === 200) {
            this.context.commit(UserSettingsMutations.ShareMySettings, payload.ShareMySettings);
        } else {
            console.log('UserSettingsActions.ShareMySettings bad response');
        }
    }

    @Action
    async UseUserSettings(payload: any) {
        const webEnvConfig = await WebEnvConfigService.getInstance().getConfigByHost();
        const res = await new SettingsService(webEnvConfig).useUserSettings(payload);

        if (res.status === 200) {
            await this.FetchSettings();
        } else {
            console.log('UserSettingsActions.ShareMySettings bad response');
        }
    }

    @Mutation
    [UserSettingsMutations.Update](userSettings: IUserSettings): void {
        this.settings = userSettings;
    }

    @Mutation
    [UserSettingsMutations.ExportAllComps](value: boolean): void {
        if (this.settings) {
            this.settings.exportAllComps = value;
        }
    }

    @Mutation
    [UserSettingsMutations.SuggestDataPreferenceUpdate](value: boolean): void {
        if (this.settings) {
            this.settings.suggestDataPreferenceUpdate = value;
        }
    }

    @Mutation
    [UserSettingsMutations.PreferredCasing](value: string): void {
        if (this.settings) {
            this.settings.preferredCasing = value;
        }
    }

    @Mutation
    [UserSettingsMutations.DataSourceColumnOrder](value: any): void {
        if (this.settings) {
            this.settings.dataSourceColumnOrder = value;
        }
    }

    @Mutation
    [UserSettingsMutations.DisplayDataCascadeColumn](value: boolean): void {
        if (this.settings) {
            this.settings.displayDataCascadeColumn = value;
        }
    }

    @Mutation
    [UserSettingsMutations.DataSourceOrderInitialized](value: boolean): void {
        if (this.settings) {
            this.settings.dataSourceOrderInitialized = value;
        }
    }

    @Mutation
    [UserSettingsMutations.SubjectCascadeDisabled](value: any): void {
        if (this.settings) {
            this.settings.subjectCascadeDisabled = value;
        }
    }

    @Mutation
    [UserSettingsMutations.UserTutorialCompleted](value: any): void {
        if (this.settings) {
            this.settings.userTutorialCompleted = value;
        }
    }

    @Mutation
    [UserSettingsMutations.ProductGuideStep](index: number): void {
        const idx = this.settings?.productGuideSteps?.findIndex((x: { id: number }) => x.id === index) ?? -1;

        if (idx !== -1) {
            this.settings!.productGuideSteps[idx]['completed'] = true;
        }
    }

    @Mutation
    [UserSettingsMutations.DisplayProductGuide](value: any): void {
        if (this.settings) {
            this.settings.displayProductGuide = value;
        }
    }

    @Mutation
    [UserSettingsMutations.ShareMySettings](value: boolean): void {
        if (this.settings) {
            this.settings.shareMySettings = value;
        }
    }

}

type UserSettingsCapitalized = {
    [Property in keyof IUserSettings as Capitalize<string & Property>]: IUserSettings[Property]
};

async function storeUserSettings(payload: Partial<IUserSettings & UserSettingsCapitalized>): Promise<AxiosResponse<any>> {
    const webEnvConfig = await WebEnvConfigService.getInstance().getConfigByHost();
    const res = await new SettingsService(webEnvConfig).saveUserSettings(payload);

    return res;
}
